/**
 * Notification Context
 * REQ-SOP-6.2: Notification Center State Management
 *
 * Provides:
 * - Notification state management
 * - Auto-refresh every 30 seconds
 * - Mark as read/delete functionality
 * - Unread count tracking
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import notificationAPI, { Notification } from '../services/notificationAPI';
import { API_BASE } from '../api';
import { tokenStorage } from '../utils/tokenStorage';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  fetchNotifications: () => Promise<void>;
  markAsRead: (notificationId: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: number) => Promise<void>;
  refreshCount: () => Promise<void>;
  // Register a callback to run whenever a notification arrives over the live
  // SSE push (e.g. a page refreshing its own session list when it's the
  // subject of a new "Added as Collaborator" notification). Returns an
  // unsubscribe function — call it on unmount.
  subscribeToLiveNotifications: (callback: () => void) => () => void;
  // Register a callback for live Project Management activity signals pushed over
  // the same SSE channel (a comment or task created/updated on a PM project the
  // user is a member of). The payload is a thin signal — the subscriber should
  // re-fetch authoritative data, scoped by pmProjectId/taskId. Returns an
  // unsubscribe function — call it on unmount.
  subscribeToPmActivity: (callback: (payload: PmActivityPayload) => void) => () => void;
}

/**
 * Signal for a live PM change. Deliberately carries only ids + the kind of
 * change, never the comment/task body — subscribers re-fetch through their own
 * (tenant-scoped) token, so the signal can never leak data.
 */
export interface PmActivityPayload {
  pmProjectId: number;
  taskId: number | null;
  event:
    | 'comment_created'
    | 'task_created'
    | 'task_updated'
    | 'task_deleted'
    | 'structure_changed'
    | string;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const liveSubscribersRef = useRef<Set<() => void>>(new Set());
  const pmActivitySubscribersRef = useRef<Set<(payload: PmActivityPayload) => void>>(new Set());

  const subscribeToLiveNotifications = useCallback((callback: () => void) => {
    liveSubscribersRef.current.add(callback);
    return () => { liveSubscribersRef.current.delete(callback); };
  }, []);

  const subscribeToPmActivity = useCallback((callback: (payload: PmActivityPayload) => void) => {
    pmActivitySubscribersRef.current.add(callback);
    return () => { pmActivitySubscribersRef.current.delete(callback); };
  }, []);

  /**
   * Fetch notifications from API
   */
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await notificationAPI.getUnread(10);
      setNotifications(response.data);
      setUnreadCount(response.unreadCount);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Refresh just the unread count (lightweight)
   */
  const refreshCount = useCallback(async () => {
    try {
      const count = await notificationAPI.getUnreadCount();
      setUnreadCount(count);
    } catch (error) {
      console.error('Failed to refresh count:', error);
    }
  }, []);

  /**
   * Mark a notification as read
   */
  const markAsRead = useCallback(async (notificationId: number) => {
    try {
      await notificationAPI.markAsRead(notificationId);

      // Update local state
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId
            ? { ...notif, is_read: true, read_at: new Date().toISOString() }
            : notif
        )
      );

      // Decrement unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
      throw error;
    }
  }, []);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationAPI.markAllAsRead();

      // Update local state
      setNotifications(prev =>
        prev.map(notif => ({
          ...notif,
          is_read: true,
          read_at: new Date().toISOString()
        }))
      );

      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      throw error;
    }
  }, []);

  /**
   * Delete a notification
   */
  const deleteNotification = useCallback(async (notificationId: number) => {
    try {
      await notificationAPI.deleteNotification(notificationId);

      // Find the notification to check if it was unread
      const notification = notifications.find(n => n.id === notificationId);
      const wasUnread = notification && !notification.is_read;

      // Remove from local state
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId));

      // Decrement unread count if it was unread
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
      throw error;
    }
  }, [notifications]);

  // Initial fetch on mount
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Auto-refresh every 30 seconds — safety-net fallback in case the SSE
  // connection below drops without reconnecting (network blip, proxy that
  // kills long-lived connections, etc.). The SSE push below is what makes a
  // new notification (e.g. "Added as Collaborator") appear instantly; this
  // poll just guarantees it's never stale for more than 30s even in the
  // worst case.
  useEffect(() => {
    const interval = setInterval(() => {
      refreshCount(); // Just refresh count, not full notifications
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [refreshCount]);

  // Live push via SSE — reaches this tab the instant a notification is
  // created server-side (see notificationStreamService.js), instead of
  // waiting up to 30s for the poll above to notice. Reconnects automatically
  // on drop (browser EventSource default behaviour) and again whenever the
  // access token changes (e.g. after a token refresh or re-login).
  useEffect(() => {
    const token = tokenStorage.getAccessToken();
    if (!token) return;

    const source = new EventSource(`${API_BASE}/api/notifications/stream?token=${encodeURIComponent(token)}`);

    source.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'notification') {
          fetchNotifications();
          liveSubscribersRef.current.forEach(cb => {
            try { cb(); } catch { /* one subscriber's error shouldn't break others */ }
          });
        } else if (data.type === 'session_collaborators_changed') {
          // Silent signal (no bell/toast) — someone else's membership on a
          // session I'm also on changed, so my own session list's
          // collaborator count/names for it are stale. Reuse the same
          // subscriber callbacks a real notification would trigger.
          liveSubscribersRef.current.forEach(cb => {
            try { cb(); } catch { /* one subscriber's error shouldn't break others */ }
          });
        } else if (data.type === 'pm_activity_changed') {
          // Silent signal — a comment/task changed on a PM project I'm a member
          // of. Fan out to PM subscribers with the ids so an open board/task
          // panel can re-fetch just what it needs. Not a bell notification.
          const payload: PmActivityPayload = {
            pmProjectId: data.pmProjectId,
            taskId: data.taskId ?? null,
            event: data.event,
          };
          pmActivitySubscribersRef.current.forEach(cb => {
            try { cb(payload); } catch { /* one subscriber's error shouldn't break others */ }
          });
        }
      } catch {
        // Ignore malformed/heartbeat frames
      }
    };

    source.onerror = () => {
      // EventSource retries on its own; nothing to do here besides letting
      // the 30s poll above cover the gap until it reconnects.
    };

    return () => source.close();
  }, [fetchNotifications]);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refreshCount,
    subscribeToLiveNotifications,
    subscribeToPmActivity
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
